/**
 * The Discover tour card's reading of a tour (Map tab, `MapTourCard`,
 * Kurtis 2026-11-05: "the photo tells the story of what to expect almost
 * more than the wording"): the two detail reads (a tourism experience, a
 * walking tour) as one shape, and from it — with the Discover item and the
 * map's route — what the card draws: the facts line, the traveler line, and
 * the route as photo rows with the walk between them.
 *
 * Pure. The card renders what this returns; `useTourDetail` fetches.
 */

import { distanceMeters, estimateWalkingMinutes } from './tourWalkability';

// family_score is 0–1; from here a tour is "Great for families" (the tour
// page's FAMILY_GREAT).
export const FAMILY_GREAT = 0.7;
// "12 min walk from your stay" up to here; further, the distance (the tour
// page's STAY_WALK_MAX_MINUTES).
export const STAY_WALK_MAX_MINUTES = 40;
// A "9 in 10 would recommend" needs this many answers behind it.
export const RECOMMEND_MIN_ANSWERS = 3;
// Two stops closer than this are the same point on the map.
const SAME_POINT_DEG = 1e-5;
// A full stop after one of these is not the end of a sentence ("St. Mark's",
// "Mt. Vesuvius", "c. 1200", "Mon. to Fri."). Lowercase, matched without case.
const ABBREVIATIONS = new Set([
  'st', 'mt', 'dr', 'mr', 'mrs', 'ms', 'ft', 'no', 'ave', 'rd', 'blvd', 'sq', 'jr', 'sr', 'prof', 'rev', 'c', 'ca', 'approx',
  'mon', 'tue', 'tues', 'wed', 'thu', 'thur', 'thurs', 'fri', 'sat', 'sun',
  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec'
]);

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const finite = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The first sentence of a description, for a one-line fact under a photo;
 * cut at `max` characters on a word boundary with an ellipsis.
 */
export function firstSentence(text, max = 160) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  let out = s;
  // The first end of a sentence: a full stop, ! or ? followed by a space and
  // a capital, a digit or an opening quote (or the end) — never the dot of
  // an abbreviation ("St. Mark's", "c. 1200") or of "1.5 km".
  const ends = /[.!?]+(?=\s+[\p{Lu}\d"“(]|$)/gu;
  let m;
  while ((m = ends.exec(s))) {
    const before = (s.slice(0, m.index).match(/(\S+)$/)?.[1] || '').replace(/^[("“]+/, '');
    if (m[0] === '.' && ABBREVIATIONS.has(before.toLowerCase())) continue;
    out = s.slice(0, m.index + m[0].length);
    break;
  }
  if (out.length > max) out = `${out.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
  // A sentence that is only its own full stop reads better without it.
  return out.replace(/\.$/, '');
}

const isWalkingMode = (contentType, mode) => contentType === 'walking_tour' || mode === 'walking';

/**
 * A tour's detail response (`TourismExperiencesService.getExperience` with
 * extras, or `WalkingToursService.getTour`) as the one shape the card reads.
 * Null for a failed read.
 *
 * The legs: an experience's stop says how long its own transit to the next
 * stop takes (`transitTimeMinutes`) and how (`transportSegmentType` — a
 * walking tour can cross a lake by boat); a walking tour's stop says how
 * long the walk TO it took (`walkingTimeMinutes`). Both become
 * `legMinutesToNext`, with `legMode` where the stop says.
 */
export function normalizeTourDetail(contentType, res) {
  if (!res?.success) return null;
  const isExperience = contentType === 'tourism_experience';
  const raw = isExperience ? res.experience : res.tour;
  if (!raw) return null;
  const rawStops = Array.isArray(res.stops) ? res.stops : [];
  const tickets = res.extras?.stopTickets || {};
  const stops = rawStops.map((s, i) => {
    const lat = finite(s.coordinates?.lat ?? s.lat);
    const lng = finite(s.coordinates?.lng ?? s.lng);
    const placed = lat != null && lng != null;
    return {
      key: String(s.stopActivityId || s.id || `stop-${i}`),
      title: s.title || null,
      description: s.description || null,
      photo: s.image || null,
      minutes: positive(s.durationMinutes),
      legMinutesToNext: isExperience ? positive(s.transitTimeMinutes) : positive(rawStops[i + 1]?.walkingTimeMinutes),
      legMode: isExperience ? (s.transportSegmentType || null) : null,
      lat: placed ? lat : null,
      lng: placed ? lng : null,
      optional: !!s.isOptionalStop,
      ticket: (s.stopActivityId && tickets[s.stopActivityId]) || null
    };
  });
  // A walking tour knows only its walking time: the time at its stops is
  // added, where the stops say how long they take.
  const atStops = stops.reduce((sum, s) => sum + (s.minutes || 0), 0);
  const walkingTotal = positive(raw.totalWalkingTimeMinutes);
  return {
    contentType,
    transportMode: raw.transportMode || null,
    description: raw.description || raw.customDescription || raw.custom_description || null,
    cover: raw.coverPhotoUrl || raw.image || null,
    difficulty: raw.difficultyLevel || null,
    durationMinutes: positive(raw.typicalDurationMinutes) || (walkingTotal ? walkingTotal + atStops : null),
    distanceMeters: positive(raw.totalDistanceMeters),
    bestTime: raw.recommendedTimeOfDay || null,
    bestSeason: raw.bestSeason || null,
    yearRound: raw.yearRound == null ? null : !!raw.yearRound,
    familyScore: finite(raw.familyScore),
    community: res.extras?.community || null,
    // Kept on one of the viewer's boards or trips' collections (lights the
    // card's bookmark).
    saved: !!raw.user_saved,
    stops
  };
}

// Each detail stop's index in the route (the pin the map numbers), matched
// in order with a cursor that only moves forward: a loop tour's last stop is
// at the same point as its first, and gets its own pin, not the first one's.
// The route lists only the stops it could place, so a stop the route lacks
// gets null and the cursor stays (the next stop may still match).
function routeIndexes(detailStops, routeStops) {
  let cursor = 0;
  return detailStops.map(s => {
    if (s.lat == null || s.lng == null) return null;
    for (let j = cursor; j < routeStops.length; j += 1) {
      const r = routeStops[j];
      if (Math.abs(r.lat - s.lat) < SAME_POINT_DEG && Math.abs(r.lng - s.lng) < SAME_POINT_DEG) {
        cursor = j + 1;
        return j;
      }
    }
    return null;
  });
}

/**
 * What the card draws.
 *
 * @param {Object} tour the Discover item ({ id, content_type, title, image, stop_count, transport_mode, rating })
 * @param {Object|null} detail from normalizeTourDetail (null while loading)
 * @param {Object|null} route { stops: [{ title, lat, lng }], stop_count, distance_meters } (null while loading)
 * @returns {{
 *   kind: 'walking'|'tour', cover: string|null, summary: string|null,
 *   stopCount: number|null, distanceMeters: number|null, durationMinutes: number|null, difficulty: string|null,
 *   bestTime: string|null, familyGreat: boolean,
 *   travelers: { rating: number|null, ratings: number, count: number, recommendTenths: number|null }|null,
 *   stops: Array<{ key, title, photo, minutes, fact, lat, lng, routeIndex: number|null, leg: { minutes, walk: boolean }|null }>
 * }}
 * A stop's `routeIndex` is the pin the map numbers it by (null when the
 * route couldn't place it); the card numbers its rows by it too, so row "3"
 * is always pin "3".
 */
export function tourCardModel({ tour, detail, route }) {
  const routeStops = route?.stops || [];
  const walking = isWalkingMode(tour?.content_type, detail?.transportMode || tour?.transport_mode);
  const kind = walking ? 'walking' : 'tour';

  // Rows come from the detail (photos, minutes, words); until it lands the
  // route alone names the stops, so the card has something to pick on.
  let base;
  if (detail?.stops?.length) {
    const indexes = routeIndexes(detail.stops, routeStops);
    base = detail.stops.map((s, i) => ({ ...s, routeIndex: indexes[i] }));
  } else {
    base = routeStops.map((r, i) => ({
      key: `route-${i}`, title: r.title || null, description: null, photo: null, minutes: null,
      legMinutesToNext: null, legMode: null, lat: r.lat, lng: r.lng, optional: false, ticket: null, routeIndex: i
    }));
  }
  const stops = base.map((s, i) => {
    const next = base[i + 1] || null;
    let leg = null;
    if (next) {
      // The leg's own mode where the stop says (a walking tour's boat
      // crossing), else the tour's.
      const legWalk = s.legMode ? s.legMode === 'walking' : walking;
      let minutes = s.legMinutesToNext;
      // Only a walk is estimated from the map: a boat or a drive takes what it takes.
      if (minutes == null && legWalk && s.lat != null && next.lat != null) {
        minutes = estimateWalkingMinutes(distanceMeters({ lat: s.lat, lng: s.lng }, { lat: next.lat, lng: next.lng }));
      }
      leg = positive(minutes) ? { minutes: Math.round(minutes), walk: legWalk } : null;
    }
    return {
      key: s.key,
      title: s.title,
      photo: s.photo,
      minutes: s.minutes,
      fact: firstSentence(s.description),
      ticket: s.ticket,
      lat: s.lat,
      lng: s.lng,
      routeIndex: s.routeIndex,
      leg
    };
  });

  const community = detail?.community || null;
  const ratings = community?.totalRatings || 0;
  const count = community ? Math.max(community.totalVisits || 0, ratings) : 0;
  const travelers = community && (count > 0 || community.avgRating != null)
    ? {
      rating: community.avgRating ?? null,
      ratings,
      count,
      recommendTenths: community.recommendRate != null && (community.recommendAnswers || 0) >= RECOMMEND_MIN_ANSWERS
        ? Math.round(community.recommendRate * 10)
        : null
    }
    : (positive(tour?.rating) ? { rating: Number(tour.rating), ratings: 0, count: 0, recommendTenths: null } : null);

  return {
    kind,
    cover: tour?.image || detail?.cover || stops.find(s => s.photo)?.photo || null,
    summary: firstSentence(detail?.description),
    stopCount: route?.stop_count ?? tour?.stop_count ?? (stops.length || null),
    distanceMeters: positive(route?.distance_meters) ?? detail?.distanceMeters ?? null,
    durationMinutes: detail?.durationMinutes ?? null,
    difficulty: detail?.difficulty || null,
    bestTime: detail?.bestTime || null,
    familyGreat: detail?.familyScore != null && detail.familyScore >= FAMILY_GREAT,
    travelers,
    stops
  };
}

/**
 * How far the tour's start is from where the traveler sleeps that night:
 * minutes on foot up to STAY_WALK_MAX_MINUTES, else the distance.
 * @returns {{ minutes: number }|{ meters: number }|null}
 */
export function stayToStart(stay, stops) {
  const start = (stops || []).find(s => s.lat != null && s.lng != null);
  const lat = finite(stay?.lat);
  const lng = finite(stay?.lng);
  if (!start || lat == null || lng == null) return null;
  const meters = distanceMeters({ lat, lng }, { lat: start.lat, lng: start.lng });
  const minutes = Math.max(1, estimateWalkingMinutes(meters));
  return minutes <= STAY_WALK_MAX_MINUTES ? { minutes } : { meters };
}
